import React from "react";
import clsx from "clsx";
import { FaCheckCircle, FaClock, FaExclamationCircle, FaLock } from "react-icons/fa";

// Utility for date diff in days
function daysBetween(start, end) {
  return Math.max(1, Math.ceil((new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24)));
}

// Utility for formatting dates
function formatDate(date) {
  return new Date(date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

const STATUS = {
  complete: {
    color: "bg-green-500",
    icon: <FaCheckCircle className="text-green-500" />,
    label: "Complete"
  },
  inprogress: {
    color: "bg-palamuru-blue",
    icon: <FaClock className="text-palamuru-blue" />,
    label: "In Progress"
  },
  delayed: {
    color: "bg-red-500",
    icon: <FaExclamationCircle className="text-red-500 animate-pulse" />,
    label: "Delayed"
  },
  notstarted: {
    color: "bg-gray-300",
    icon: <FaLock className="text-gray-400" />,
    label: "Not Started"
  }
};

export default function ConstructionTimeline({ phases = [] }) {
  console.log('🏗️ ConstructionTimeline received phases:', phases);
  console.log('🏗️ First phase details:', phases[0]);
  
  // Timeline summary
  const completed = phases.filter(p => {
    const isCompleted = (p.actualProgressPercent ?? 0) >= 100 || 
                       p.status === "Completed" || 
                       p.status === "Complete";
    console.log(`🏗️ Phase "${p.phase}" - status: ${p.status}, progress: ${p.actualProgressPercent}, isCompleted: ${isCompleted}`);
    return isCompleted;
  }).length;
  const delayed = phases.filter(p => {
    const plannedEnd = new Date(p.plannedEnd);
    const today = new Date();
    const isCompleted = (p.actualProgressPercent ?? 0) >= 100 || 
                       p.status === "Completed" || 
                       p.status === "Complete";
    return (!isCompleted && p.actualProgressPercent < 100 && today > plannedEnd) || (p.actualEnd && new Date(p.actualEnd) > plannedEnd);
  }).length;
  const total = phases.length;
  const handover = phases[phases.length - 1]?.plannedEnd;
  const currentIdx = phases.findIndex(p => {
    const isCompleted = (p.actualProgressPercent ?? 0) >= 100 || 
                       p.status === "Completed" || 
                       p.status === "Complete";
    return !isCompleted;
  });

  // Legend
  const legend = [
    { color: STATUS.complete.color, label: "Complete" },
    { color: STATUS.inprogress.color, label: "In Progress" },
    { color: STATUS.delayed.color, label: "Delayed" },
    { color: STATUS.notstarted.color, label: "Not Started" }
  ];

  return (
    <div className="w-full">
      {/* Summary */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-2">
        <div className="font-body text-gray-700 text-sm">
          <span className="font-body-semibold text-palamuru-blue">{completed}/{total}</span> phases complete
          {delayed > 0 && <span className="ml-4 text-red-500 font-body-semibold">{delayed} delayed</span>}
          {handover && <span className="ml-4 text-gray-500">Est. Handover: <span className="font-body-semibold text-palamuru-gold">{formatDate(handover)}</span></span>}
        </div>
        <div className="flex items-center gap-4 text-xs">
          {legend.map(l => (
            <span key={l.label} className="flex items-center gap-1">
              <span className={clsx(l.color, "inline-block w-3 h-3 rounded-full mr-1")}></span>{l.label}
            </span>
          ))}
        </div>
      </div>
      {/* Timeline */}
      <div className="overflow-x-auto pb-2">
        <div className="min-w-[900px] flex gap-4 pt-6" style={{ overflow: 'visible' }}>
          {phases.map((phase, i) => {
            // Status logic
            const plannedEndDate = new Date(phase.plannedEnd);
            const today = new Date();
            let status = "notstarted";
            
            // Check if phase is completed based on status or progress
            const isCompleted = (phase.actualProgressPercent ?? 0) >= 100 || 
                               phase.status === "Completed" || 
                               phase.status === "Complete";
            
            console.log(`🏗️ Phase "${phase.phase}" status calculation:`, {
              name: phase.phase,
              status: phase.status,
              progress: phase.actualProgressPercent,
              isCompleted,
              finalStatus: status
            });
            
            if (isCompleted) {
              status = "complete";
              if (phase.actualEnd && new Date(phase.actualEnd) > plannedEndDate) status = "delayed";
              if (phase.actualEnd && new Date(phase.actualEnd) < plannedEndDate) status = "complete";
            } else if (phase.actualProgressPercent > 0) {
              status = today > plannedEndDate ? "delayed" : "inprogress";
            } else if (today > plannedEndDate) {
              status = "delayed";
            }
            const isCurrent = i === currentIdx;
            // Tooltip for phase name
            const phaseNameTooltip = (
              <div className="text-xs text-left p-2 bg-white rounded shadow border border-gray-200 min-w-[120px] max-w-[250px]">
                {phase.phase}
              </div>
            );
            // Tooltip for card details
            const tooltip = (
              <div className="text-xs text-left p-3 bg-white rounded-xl shadow-lg border border-gray-100 min-w-[200px]">
                <div className="font-display-semibold text-gray-900 mb-1">{phase.phase}</div>
                <div className="mb-1"><span className="font-body-semibold">Planned:</span> {formatDate(phase.plannedStart)} → {formatDate(phase.plannedEnd)}</div>
                <div className="mb-1"><span className="font-body-semibold">Actual:</span> {phase.actualStart ? formatDate(phase.actualStart) : '--'} → {isCompleted ? (phase.actualEnd ? formatDate(phase.actualEnd) : '100%') : `${phase.actualProgressPercent ?? 0}%`}</div>
                {status === "delayed" && phase.delayReason && (
                  <div className="text-red-500 mt-1"><span className="font-body-semibold">Delay:</span> {phase.delayReason}</div>
                )}
              </div>
            );
            return (
              <div
                key={phase.phase}
                className={clsx(
                  "relative flex flex-col items-center group min-w-[180px] max-w-[200px] flex-1 transition-transform duration-200",
                  isCurrent && "z-20",
                )}
                style={{ zIndex: isCurrent ? 20 : 1, position: 'relative' }}
                tabIndex={0}
                aria-label={`Phase: ${phase.phase}`}
                onMouseEnter={e => e.currentTarget.style.zIndex = 20}
                onMouseLeave={e => e.currentTarget.style.zIndex = isCurrent ? 20 : 1}
              >
                {/* Card */}
                <div
                  className={clsx(
                    "w-full rounded-2xl shadow-soft border border-gray-100 bg-white px-4 pt-4 pb-2 flex flex-col items-center transition-all duration-300",
                    isCurrent && "ring-2 ring-palamuru-blue/60 z-20",
                    "group-hover:scale-105 group-hover:shadow-2xl group-hover:z-20 group-focus:scale-105 group-focus:shadow-2xl group-focus:z-20",
                  )}
                  style={{
                    transform: isCurrent ? 'scale(1.03)' : undefined,
                    boxShadow: isCurrent ? '0 8px 32px 0 rgba(80, 120, 200, 0.15)' : undefined,
                    zIndex: isCurrent ? 20 : 1,
                    position: 'relative',
                  }}
                >
                  <div className="flex items-center gap-2 mb-2 w-full justify-center">
                    <span>{STATUS[status].icon}</span>
                    <span
                      className="font-body-semibold text-gray-800 text-sm truncate max-w-[110px] cursor-pointer relative group"
                      tabIndex={0}
                    >
                      {phase.phase}
                      <span className="absolute left-1/2 -translate-x-1/2 top-7 z-30 opacity-0 group-hover:opacity-100 group-focus:opacity-100 pointer-events-none group-hover:pointer-events-auto group-focus:pointer-events-auto transition-all duration-200">
                        {phaseNameTooltip}
                      </span>
                    </span>
                    {isCurrent && <span className="ml-2 animate-pulse w-2 h-2 rounded-full bg-palamuru-blue"></span>}
                  </div>
                  {/* Progress Bar */}
                  <div className="w-full h-4 bg-gray-200 rounded-full relative mb-2">
                    <div
                      className={clsx(
                        "absolute left-0 top-0 h-4 rounded-full transition-all duration-500",
                        STATUS[status].color,
                        isCurrent && "shadow-lg"
                      )}
                      style={{ width: `${Math.min(100, isCompleted ? 100 : (phase.actualProgressPercent ?? 0))}%` }}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-body-semibold text-gray-700">
                      {isCompleted ? 100 : (phase.actualProgressPercent ?? 0)}%
                    </span>
                  </div>
                  {/* Dates */}
                  <div className="flex flex-col w-full text-[11px] text-gray-500 font-body mb-1 items-center">
                    <span>{formatDate(phase.plannedStart)}</span>
                    <span>{formatDate(phase.plannedEnd)}</span>
                  </div>
                </div>
                {/* Tooltip (hover/focus/click) */}
                <div className="absolute left-1/2 -translate-x-1/2 -top-2 z-30 opacity-0 group-hover:opacity-100 group-focus:opacity-100 pointer-events-none group-hover:pointer-events-auto group-focus:pointer-events-auto transition-all duration-200">
                  {tooltip}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
} 